import { useState, useRef, useEffect } from "react";

const ProductImage = ({ imgs }) => {
  const [selectedImage, setSelectedImage] = useState(imgs[0]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showZoom, setShowZoom] = useState(false);
  const imageRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const zoomBoxRef = useRef(null);
  
  // Size of the magnifier lens (the box around the cursor)
  const lensSize = 100;
  // Zoom level for the magnified view
  const zoomLevel = 2.5;

  // Check if device is mobile or tablet
  useEffect(() => {
    const checkDeviceSize = () => {
      setIsMobileOrTablet(window.innerWidth < 1024); // Threshold for mobile/tablet
    };
    
    checkDeviceSize();
    window.addEventListener('resize', checkDeviceSize);
    
    return () => {
      window.removeEventListener('resize', checkDeviceSize);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (imageRef.current && !isMobileOrTablet) {
      const { left, top, width, height } = imageRef.current.getBoundingClientRect();
      
      // Calculate cursor position relative to the image (as percentages)
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;
      setPosition({ x, y });
      
      // Calculate cursor position in pixels for the lens positioning
      const cursorX = e.clientX - left;
      const cursorY = e.clientY - top;
      
      // Adjust lens position to be centered on cursor
      let lensX = cursorX - lensSize / 2;
      let lensY = cursorY - lensSize / 2;
      
      // Keep lens within image boundaries
      lensX = Math.max(0, Math.min(width - lensSize, lensX));
      lensY = Math.max(0, Math.min(height - lensSize, lensY));
      
      setCursorPosition({ x: lensX, y: lensY });
      
      // Show zoom whenever hovering over the image (removed right side restriction)
      if (!showZoom) {
        setShowZoom(true);
      }
    }
  };

  // Handle mouse enter to ensure zoom shows
  const handleMouseEnter = () => {
    if (!isMobileOrTablet) {
      setShowZoom(true);
    }
  };

  // Prevent zoom from closing unexpectedly when mouse moves over the zoom box
  const handleZoomAreaMouseEnter = () => {
    if (!isMobileOrTablet) {
      setShowZoom(true);
    }
  };

  // Keep showing zoom when mouse is over either the main image or the zoom box
  const handleMouseLeave = (e) => {
    if (!isMobileOrTablet) {
      // Check if we're leaving to the zoom box
      if (zoomBoxRef.current && zoomBoxRef.current.contains(e.relatedTarget)) {
        return; // Don't hide zoom if mouse moves to zoom box
      }
      setShowZoom(false);
    }
  };

  return (
    <div className="flex flex-col-reverse xl:flex-row items-start gap-4 w-full">
      {/* Image Gallery */}
      <div className="flex flex-row xl:flex-col gap-4 xl:w-24">
        {imgs.map((img, index) => (
          <img
            key={index}
            src={img}
            alt={`Thumbnail ${index + 1}`}
            className={`aspect-square xl:w-24 xl:h-20 size-12 sm:size-16 md:size-16 lg:size-24 object-cover cursor-pointer hover:scale-105 shadow-md border border-gray-200 border-opacity-10 ${
              selectedImage === img ? "border-black" : ""
            }`}
            onClick={() => setSelectedImage(img)}
          />
        ))}
      </div>

      {/* Main Image Container */}
      <div className="relative w-full">
        {/* Main Image with Lens */}
        <div 
          className="aspect-square w-full relative overflow-hidden shadow-md border border-gray-200 border-opacity-20"
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={imageRef}
        >
          <img
            src={selectedImage}
            alt="Selected"
            className="aspect-square w-full object-cover cursor-crosshair"
          />
          
          {/* Magnifier Lens (box around cursor) - only on desktop */}
          {!isMobileOrTablet && showZoom && (
            <div 
              className="absolute border-2 border-white pointer-events-none shadow-lg"
              style={{
                width: `${lensSize}px`,
                height: `${lensSize}px`,
                left: `${cursorPosition.x}px`,
                top: `${cursorPosition.y}px`,
              }}
            ></div>
          )}
        </div>
        
        {/* Magnified View Box (positioned absolutely to the side) - only on desktop */}
        {!isMobileOrTablet && showZoom && (
          <div 
            ref={zoomBoxRef}
            className="absolute top-0 left-full ml-4 aspect-square w-72 h-72 overflow-hidden shadow-lg border border-gray-300 z-10"
            onMouseEnter={handleZoomAreaMouseEnter}
          >
            <div 
              className="w-full h-full relative"
              style={{
                backgroundImage: `url(${selectedImage})`,
                backgroundPosition: `${position.x}% ${position.y}%`,
                backgroundSize: `${zoomLevel * 100}%`,
                backgroundRepeat: 'no-repeat',
              }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductImage;